import { Switch, Route, Redirect } from 'react-router-dom';

import { CycleEvaluationFormNavigator } from './CycleEvaluation/CycleEvaluationFormNavigator';
import { CycleEvaluationGroupFormNavigator } from './CycleEvaluation/CycleEvaluationGroupFormNavigator';
import { Evaluation360FormNavigator } from './Evaluation360/Evaluation360FormNavigator';
import { WithPermission } from './PermissionGuard';
import { SelfAssessmentFormNavigator } from './SelfAssessment/SelfAssessmentFormNavigator';
import { RouteName } from './types';

import type { UserPermissions } from '@/types/directory/User';

import { FeatureDisabledState } from '@/components/FeatureDisabledState/FeatureDisabledState';
import { FeatureGate } from '@/components/FeatureGate/FeatureGate';
import { NotFoundState } from '@/components/NotFoundState/NotFoundState';
import { SurveyRouteWrapper } from '@/components/SurveyRouteWrapper/SurveyRouteWrapper';
import VisionValuesPage from '@/components/VisionValues/VisionValuesPage';
import { FeatureFlag } from '@/env';
import { CompetenciesPage } from '@/pages/Competencies/CompetenciesPage';
import { CompetencyProfileWizardPage } from '@/pages/Competencies/CompetencyProfileWizardPage';
import { CompetencyProfilesAdminPage } from '@/pages/CompetencyProfilesAdmin/CompetencyProfilesAdminPage';
import { DashboardPage as CycleEvaluationDashboardPage } from '@/pages/CycleEvaluation/Dashboard/DashboardPage';
import { LaunchSummaryPage } from '@/pages/CycleEvaluation/LaunchSummary/LaunchSummaryPage';
import { TranslateQuestionnairePage as CycleEvalTranslateQuestionnairePage } from '@/pages/CycleEvaluation/TranslateQuestionnaire/TranslateQuestionnairePage';
import { EmployeePortalPage } from '@/pages/EmployeePortal/EmployeePortalPage';
import { DashboardPage } from '@/pages/Evaluation360/Dashboard/DashboardPage';
import { TranslateQuestionnairePage as Eval360TranslateQuestionnairePage } from '@/pages/Evaluation360/TranslateQuestionnaire/TranslateQuestionnairePage';
import { ManagerAssessmentEvaluationPage } from '@/pages/ManagerAssessment/Evaluation/ManagerAssessmentEvaluationPage';
import { ManagerAssessmentMeetPage } from '@/pages/ManagerAssessment/Meet/ManagerAssessmentMeetPage';
import { ManagerAssessmentPrepareMeetingPage } from '@/pages/ManagerAssessment/PrepareMeeting/ManagerAssessmentPrepareMeetingPage';
import ManagerAssessmentResultsPage from '@/pages/ManagerAssessment/Results/ManagerAssessmentResultsPage';
import { ManagerAssessmentSetupPage } from '@/pages/ManagerAssessment/Setup/ManagerAssessmentSetupPage';
import { ManageTemplatesPage } from '@/pages/ManageTemplates/ManageTemplatesPage';
import { MeetingPlanTemplateEditorPage } from '@/pages/ManageTemplates/MeetingPlanTemplate/MeetingPlanTemplateEditorPage';
import { TemplateQuestionnaireCreationPage } from '@/pages/ManageTemplates/TemplateQuestionnaire/TemplateQuestionnaireCreationPage';
import { TranslateTemplatePage } from '@/pages/ManageTemplates/TranslateTemplate/TranslateTemplatePage';
import CreateObjectiveTypePage from '@/pages/Objectives/CreateObjectiveTypePage';
import EmployeesWithoutObjectivesPage from '@/pages/Objectives/EmployeesWithoutObjectives/EmployeesWithoutObjectivesPage';
import ObjectiveDetailPage from '@/pages/Objectives/ObjectiveDetailPage';
import ObjectiveFormPage from '@/pages/Objectives/ObjectiveFormPage';
import ObjectivesPage from '@/pages/Objectives/ObjectivesPage';
import PillarObjectivesPage from '@/pages/Objectives/PillarObjectivesPage';
import { PerformanceProfile } from '@/pages/PerformanceProfile';
import { CalibrationReportPage } from '@/pages/Reports/Calibration/CalibrationReportPage';
import { CompensationReportPage } from '@/pages/Reports/Compensation/CompensationReportPage';
import { CompetencyMatrixReportPage } from '@/pages/Reports/CompetencyMatrix/CompetencyMatrixReportPage';
import ResultsPage from '@/pages/Results/ResultsPage';
import { SuccessMessagePage } from '@/pages/shared/SuccessMessage/SuccessMessagePage';
import { SurveyAnswerPage } from '@/pages/SurveyAnswer/SurveyAnswer/SurveyAnswerPage';
import { ThematicMeetingPlanPage } from '@/pages/ThematicMeetings/Plan/ThematicMeetingPlanPage';
import { ThematicMeetingsPage } from '@/pages/ThematicMeetings/ThematicMeetingsPage';

// The host hands this module every address under its prefixes, so its own `/error/404` never sees
// an address we do not serve: without this last route the module would render an empty page, which
// reads as a broken screen rather than as a dead link.
const ROUTE_NOT_FOUND = (
    <NotFoundState
        titleKey={'errors.pageNotFoundTitle'}
        messageKey={'errors.pageNotFoundMessage'}
    />
);

const REPORTS_DISABLED = (
    <FeatureDisabledState
        flag={FeatureFlag.REPORTS}
        titleKey={'reports.disabled.title'}
        messageKey={'reports.disabled.message'}
    />
);

// Both thematic-meeting routes share one permission scope; a module-level constant keeps them in
// step and avoids handing `WithPermission` a brand-new array on every render.
const THEMATIC_MEETINGS_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageTeamPerformance',
    'canManageAllDepartmentsPerformance',
];

// Mirrors the backend's `ReportAccessScope.EnsureCanViewReports`: either performance-management
// scope is enough to view a report.
const REPORTS_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageTeamPerformance',
    'canManageAllDepartmentsPerformance',
];

// The evaluation-cycles dashboard has two audiences: the cycle administrators, and the objectives
// managers who come for its "Objective periods" tab. The dashboard itself shows each reader only
// the tabs their scope covers.
const CYCLE_DASHBOARD_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageAllDepartmentsPerformance',
    'canManageAllEmployeesObjectives',
];

// Mirrors the backend's `CanAccessObjectives`, which gates writing objectives and the pillar summaries:
// the whole company, the caller's team, or their own objectives. Reading needs none of them --
// every employee reads the objectives they hold -- so the list and detail pages sit behind the flag
// alone, and the server narrows what each reader sees. The pillar page stands on its pillar's totals.
const OBJECTIVES_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageAllEmployeesObjectives',
    'canManageTeamObjectives',
    'canManageTeamPerformance',
    'canEditOwnObjectives',
];

// Mirrors the backend's `CanManageOthersObjectives`: the manager-facing tools of the module.
const OBJECTIVE_MANAGERS_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageAllEmployeesObjectives',
    'canManageTeamObjectives',
    'canManageTeamPerformance',
];

// The calibration backend checks the same two performance-management scopes: a team manager sees
// their own team's calibration, an all-departments manager sees every team's.
const CALIBRATION_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageTeamPerformance',
    'canManageAllDepartmentsPerformance',
];

export const RootNavigator = () => {
    return (
        <Switch>
            <Redirect
                exact
                from={'/'}
                to={RouteName.Evaluation360}
            />

            <Route
                exact
                path={RouteName.CompetencyProfileCreate}
                render={() => (
                    <WithPermission permissionKey={'canManageCompetencies'}>
                        <CompetencyProfileWizardPage />
                    </WithPermission>
                )}
            />

            <Route
                path={RouteName.CompetencyProfilesAdmin}
                render={() => (
                    <WithPermission permissionKey={'canAssignCompetencyProfiles'}>
                        <CompetencyProfilesAdminPage />
                    </WithPermission>
                )}
            />

            <Route
                path={RouteName.Competencies}
                render={() => (
                    <WithPermission permissionKey={'canManageCompetencies'}>
                        <CompetenciesPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.VisionValues}
                render={() => (
                    <WithPermission permissionKey={'canManageCompanyValues'}>
                        <VisionValuesPage />
                    </WithPermission>
                )}
            />

            {/* Every employee with access to the module reads their own portal: no management
                permission, and each tab asks the server for the reader's own data only. */}
            <Route
                exact
                path={RouteName.EmployeePortal}
                render={() => <EmployeePortalPage />}
            />

            <Route
                exact
                path={RouteName.Objectives}
                render={() => <ObjectivesPage />}
            />

            <Route
                exact
                path={RouteName.ObjectivePillar}
                render={() => (
                    <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                        <PillarObjectivesPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectivesNew}
                render={() => (
                    <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                        <CreateObjectiveTypePage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectivesNewType}
                render={() => (
                    <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                        <ObjectiveFormPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectiveEdit}
                render={() => (
                    <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                        <ObjectiveFormPage />
                    </WithPermission>
                )}
            />

            {/* Before `ObjectiveDetail`: `/objectives/:id` would otherwise swallow this path. */}
            <Route
                exact
                path={RouteName.ObjectivesWithoutObjective}
                render={() => (
                    <WithPermission anyOfPermissionKeys={OBJECTIVE_MANAGERS_PERMISSIONS}>
                        <EmployeesWithoutObjectivesPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectiveDetail}
                render={() => <ObjectiveDetailPage />}
            />

            <Route
                exact
                path={RouteName.CycleEvaluation}
                render={() => (
                    <WithPermission anyOfPermissionKeys={CYCLE_DASHBOARD_PERMISSIONS}>
                        <CycleEvaluationDashboardPage />
                    </WithPermission>
                )}
            />

            <Route
                path={RouteName.CycleEvaluationQuestionnaireTranslation}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <CycleEvalTranslateQuestionnairePage />
                    </WithPermission>
                )}
            />

            <Route
                path={`${RouteName.CycleEvaluationGroupForm}/:step/:cycleEvaluationGroupId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <CycleEvaluationGroupFormNavigator />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={`${RouteName.CycleEvaluationGroupLaunchSummary}/:cycleEvaluationGroupId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <LaunchSummaryPage />
                    </WithPermission>
                )}
            />

            <Route
                path={`${RouteName.CycleEvaluationForm}/:step/:cycleEvaluationId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <CycleEvaluationFormNavigator />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.Evaluation360}
                component={DashboardPage}
            />

            <Route
                path={RouteName.SelfAssessmentQuestionnaireTranslation}
                component={Eval360TranslateQuestionnairePage}
            />

            <Route
                path={RouteName.Eval360QuestionnaireTranslation}
                component={Eval360TranslateQuestionnairePage}
            />

            <Route
                path={`${RouteName.Eval360Form}/:step/:cycleInstanceId`}
                component={Evaluation360FormNavigator}
            />

            <Route
                path={`${RouteName.SelfAssessmentForm}/:step/:cycleInstanceId`}
                component={SelfAssessmentFormNavigator}
            />

            <Route
                path={`${RouteName.SurveyAnswer}/:reviewAnswerId`}
                render={() => (
                    <SurveyRouteWrapper>
                        <SurveyAnswerPage />
                    </SurveyRouteWrapper>
                )}
            />

            <Route
                path={`${RouteName.SurveyAnswer}`}
                render={() => (
                    <SurveyRouteWrapper>
                        <SurveyAnswerPage />
                    </SurveyRouteWrapper>
                )}
            />

            <Route
                path={`${RouteName.TemplateTranslation}/:templateId`}
                component={TranslateTemplatePage}
            />

            <Route
                path={`${RouteName.TemplateQuestionnaire}/:templateId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <TemplateQuestionnaireCreationPage />
                    </WithPermission>
                )}
            />

            {/* Same capability as the Templates page it is reached from, and the same the write
                route asks for: a manager of one team may read the company's plans, never reword them. */}
            <Route
                path={`${RouteName.MeetingPlanTemplate}/:templateId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <MeetingPlanTemplateEditorPage />
                    </WithPermission>
                )}
            />

            {/* Administering templates is an all-departments capability: the page reads
                Amelio's app-wide library, which the API serves to that scope only. A team
                manager reaching it fired one refused request per evaluation type (#14600). */}
            <Route
                path={`${RouteName.ManageTemplates}`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <ManageTemplatesPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.ManagerAssessment}
                component={ManagerAssessmentSetupPage}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentEvaluation}/:cycleInstanceId`}
                component={ManagerAssessmentEvaluationPage}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentResults}/:cycleInstanceId`}
                component={ManagerAssessmentResultsPage}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentPrepareMeeting}/:cycleInstanceId`}
                component={ManagerAssessmentPrepareMeetingPage}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentMeet}/:cycleInstanceId`}
                component={ManagerAssessmentMeetPage}
            />

            <Route
                path={RouteName.SuccessMessage}
                component={SuccessMessagePage}
            />

            <Route
                path={`${RouteName.Eval360Results}/:reviewInstanceId`}
                component={ResultsPage}
            />

            <Route
                path={`${RouteName.PeriodResults}/:employeeId/:reviewType`}
                component={ResultsPage}
            />

            <Route
                path={RouteName.PerformanceProfile}
                component={PerformanceProfile}
            />

            <Route
                exact
                path={RouteName.ThematicMeetings}
                render={() => (
                    <WithPermission anyOfPermissionKeys={THEMATIC_MEETINGS_PERMISSIONS}>
                        <ThematicMeetingsPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={`${RouteName.ThematicMeetings}/:meetingId`}
                render={() => (
                    <WithPermission anyOfPermissionKeys={THEMATIC_MEETINGS_PERMISSIONS}>
                        <ThematicMeetingPlanPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.CompetencyMatrixReport}
                render={() => (
                    <FeatureGate
                        flag={FeatureFlag.REPORTS}
                        fallback={REPORTS_DISABLED}
                    >
                        <WithPermission anyOfPermissionKeys={REPORTS_PERMISSIONS}>
                            <CompetencyMatrixReportPage />
                        </WithPermission>
                    </FeatureGate>
                )}
            />

            <Route
                exact
                path={RouteName.CalibrationReport}
                render={() => (
                    <FeatureGate
                        flag={FeatureFlag.CALIBRATION_REPORT}
                        fallback={
                            <FeatureDisabledState
                                flag={FeatureFlag.CALIBRATION_REPORT}
                                titleKey={'calibration.disabled.title'}
                                messageKey={'calibration.disabled.message'}
                            />
                        }
                    >
                        <WithPermission anyOfPermissionKeys={CALIBRATION_PERMISSIONS}>
                            <CalibrationReportPage />
                        </WithPermission>
                    </FeatureGate>
                )}
            />

            <Route
                exact
                path={RouteName.CompensationReport}
                render={() => (
                    <FeatureGate
                        flag={FeatureFlag.REPORTS}
                        fallback={REPORTS_DISABLED}
                    >
                        <FeatureGate
                            flag={FeatureFlag.COMPENSATION_PLANNING}
                            fallback={
                                <FeatureDisabledState
                                    flag={FeatureFlag.COMPENSATION_PLANNING}
                                    titleKey={'compensation.disabled.title'}
                                    messageKey={'compensation.disabled.message'}
                                />
                            }
                        >
                            <WithPermission anyOfPermissionKeys={REPORTS_PERMISSIONS}>
                                <CompensationReportPage />
                            </WithPermission>
                        </FeatureGate>
                    </FeatureGate>
                )}
            />

            {/* Last on purpose: a pathless route matches whatever none of the above did. */}
            <Route render={() => ROUTE_NOT_FOUND} />
        </Switch>
    );
};
